import React, { createContext, useContext, ReactNode } from 'react';
import { useSystemHealth, SystemHealthState, HealthIndicatorProps, getHealthIndicatorProps } from '../hooks/useSystemHealth';
import { CircuitState } from '../utils/circuitBreaker';

/**
 * @module SystemHealthContext
 * @description
 * Global context for system health state.
 * Provides offline detection and circuit breaker status to all components.
 */

interface SystemHealthContextType extends SystemHealthState {
  /** Manually trigger a health check */
  checkNow: () => Promise<boolean>;
  /** Get indicator props for the health indicator component */
  getIndicatorProps: () => HealthIndicatorProps;
  /** Check if a specific service circuit is open */
  isServiceDegraded: (serviceName: string) => boolean;
  /** Get circuit state for a specific service */
  getCircuitState: (serviceName: string) => CircuitState | undefined;
}

const SystemHealthContext = createContext<SystemHealthContextType | null>(null);

/**
 * Provider component for system health state
 */
export function SystemHealthProvider({ children }: { children: ReactNode }) {
  const health = useSystemHealth();

  const getIndicatorProps = () => getHealthIndicatorProps(health);

  const isServiceDegraded = (serviceName: string): boolean => {
    const state = health.circuitStates[serviceName];
    return state === 'OPEN';
  };

  const getCircuitState = (serviceName: string): CircuitState | undefined => {
    return health.circuitStates[serviceName];
  };

  const value: SystemHealthContextType = {
    ...health,
    getIndicatorProps,
    isServiceDegraded,
    getCircuitState,
  };

  return (
    <SystemHealthContext.Provider value={value}>
      {children}
    </SystemHealthContext.Provider>
  );
}

/**
 * Hook to access system health state
 */
export function useSystemHealthContext(): SystemHealthContextType {
  const context = useContext(SystemHealthContext);
  if (!context) {
    throw new Error('useSystemHealthContext must be used within SystemHealthProvider');
  }
  return context;
}

/**
 * Hook for components that just need online/offline status
 */
export function useOnlineStatus(): { isOnline: boolean; isDegraded: boolean } {
  const context = useContext(SystemHealthContext);
  if (!context) {
    // Fallback for components outside provider
    return { isOnline: true, isDegraded: false };
  }
  return { isOnline: context.isOnline, isDegraded: context.isDegraded };
}

export default SystemHealthContext;
